import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsRight } from "lucide-react"
import type { ReactNode } from "react"
import { useCollapseDetailPanel } from "../master-data/DetailPanelCollapseContext"
import { SectionLabel } from "./SectionLabel"
import { useElementWidth } from "./useElementWidth"

interface DetailPanelHeaderProps {
  label: string
  title: string
  subtitle?: string
  /**
   * Right-aligned actions. `iconOnly` turns true once this title and the labelled
   * actions no longer fit one row — drop the button text then, keeping aria-label/title
   * so the action stays named.
   */
  actions?: (state: { iconOnly: boolean }) => ReactNode
}

/**
 * Header shared by the detail-pane surfaces: label + title + subtitle on the left,
 * actions and the panel-collapse button on the right.
 *
 * What the header gives up as it narrows is measured, not guessed. Offscreen copies of
 * the labelled and the icon-only row (this title at its natural width included) are
 * compared against the space actually available, so a long supplier name or a longer
 * translation drops the labels exactly when it has to: labels first, and only once even
 * the icons stop fitting does the row wrap. Wrapping stays off until then — otherwise
 * CSS would wrap the labelled row on the spot and the measurement would unwrap it a
 * frame later, which reads as a flicker.
 */
export function DetailPanelHeader({
  label,
  title,
  subtitle,
  actions,
}: DetailPanelHeaderProps) {
  const { translate } = useTranslation()
  const collapseDetailPanel = useCollapseDetailPanel()
  const { ref: headerRef, width: available } = useElementWidth<HTMLDivElement>()
  const { ref: labelledRef, width: labelledWidth } =
    useElementWidth<HTMLDivElement>()
  const { ref: iconOnlyRef, width: iconOnlyWidth } =
    useElementWidth<HTMLDivElement>()

  // Unmeasured → treat as fitting, so the first paint shows labels on one row rather
  // than flashing through the collapsed state.
  const fits = (needed: number | null) =>
    needed === null || available === null || needed <= available
  const iconOnly = !fits(labelledWidth)
  const allowWrap = !fits(iconOnlyWidth)

  const collapseButton = collapseDetailPanel && (
    <Button
      size="sm"
      variant="outline"
      className="h-[30px] w-[30px]"
      onClick={collapseDetailPanel}
      aria-label={translate("MC_MD_DETAIL_COLLAPSE_PANEL_BTN")}
    >
      <ChevronsRight size={14} />
    </Button>
  )

  // `measuring` renders the title at its natural width for the probes; on screen it
  // truncates instead, so a long name ellipsizes rather than widening the row.
  const titleBlock = (measuring?: boolean) => (
    <div className={cn(measuring ? "whitespace-nowrap" : "min-w-0 grow")}>
      <SectionLabel className="mb-1">{label}</SectionLabel>
      <h2
        className={cn(
          "text-xl font-semibold text-white",
          !measuring && "truncate",
        )}
        title={measuring ? undefined : title}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={cn(
            "font-mono mt-0.5 text-xs text-grey5",
            !measuring && "truncate",
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  )

  /**
   * Offscreen copy of one row, used only for measurement. `invisible` keeps it out of
   * the tab order and the a11y tree while still giving it real dimensions, and it
   * re-renders the caller's own `actions`, so nothing is duplicated by hand.
   */
  const probe = (
    ref: (element: HTMLDivElement | null) => void,
    probeIconOnly: boolean,
  ) => (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-start gap-3"
    >
      {titleBlock(true)}
      <div className="flex items-center gap-2">
        {actions?.({ iconOnly: probeIconOnly })}
        {collapseButton}
      </div>
    </div>
  )

  return (
    <div
      ref={headerRef}
      className={cn(
        "relative flex items-start gap-3 border-b border-grey4 px-4 py-4",
        allowWrap ? "flex-wrap" : "flex-nowrap",
      )}
    >
      {titleBlock()}
      {/* Shrinkable on purpose: shrink-0 would pin this at max-content width and
          overflow the panel instead of wrapping. Its children keep their own size. */}
      <div
        className={cn(
          "ml-auto flex min-w-0 items-center justify-end gap-2 *:shrink-0",
          allowWrap ? "flex-wrap" : "flex-nowrap",
        )}
      >
        {actions?.({ iconOnly })}
        {collapseButton}
      </div>

      {probe(labelledRef, false)}
      {probe(iconOnlyRef, true)}
    </div>
  )
}
